import React, { useState, useRef, useEffect } from 'react';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import Image from 'components/Image';
import { ImageOwnerType, ImageSize } from 'app-constants';
import styles from 'components/ImageUpload.module.css';
import globalStyles from 'Global.module.css';

interface ImageUploadProps {
    ownerType: ImageOwnerType;
    displaySize: ImageSize;
    imageId: string | null | undefined;
    ownerId: string;
    onImageChange: (uploadData: { uploadInput: any; file: File } | null) => void;
    aspectRatio: number;
}

const ImageUpload: React.FC<ImageUploadProps> = ({ ownerType, displaySize, imageId, ownerId, onImageChange, aspectRatio }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const cropWidth = 90;
    const cropHeight = cropWidth / aspectRatio;
    const [crop, setCrop] = useState<Crop>({
        unit: '%',
        width: cropWidth,
        height: cropHeight,
        x: 5,
        y: (100 - cropHeight) / 2,
    });
    const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
    const imgRef = useRef<HTMLImageElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const handleChooseImage = () => {
        setIsModalOpen(true);
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            setSelectedFile(file);

            const reader = new FileReader();
            reader.addEventListener('load', () => {
                setImageSrc(reader.result as string);
            });
            reader.readAsDataURL(file);
        }
    };

    const handleSave = () => {
        if (!selectedFile || !completedCrop || !imgRef.current) {
            alert('Please select an image and set a crop area');
            return;
        }

        const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
        const scaleY = imgRef.current.naturalHeight / imgRef.current.height;

        const uploadInput = {
            ownerId: ownerId,
            ownerType: ownerType,
            contentType: selectedFile.type,
            crop_left: Math.round(completedCrop.x * scaleX),
            crop_top: Math.round(completedCrop.y * scaleY),
            crop_width: Math.round(completedCrop.width * scaleX),
            crop_height: Math.round(completedCrop.height * scaleY),
        };

        generateCroppedPreview(completedCrop, scaleX, scaleY);
        onImageChange({ uploadInput, file: selectedFile });
        handleCancel();
    };

    const generateCroppedPreview = (crop: PixelCrop, scaleX: number, scaleY: number) => {
        if (!imgRef.current) return;

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const cropX = crop.x * scaleX;
        const cropY = crop.y * scaleY;
        const cropWidth = crop.width * scaleX;
        const cropHeight = crop.height * scaleY;

        canvas.width = cropWidth;
        canvas.height = cropHeight;

        ctx.drawImage(
            imgRef.current,
            cropX,
            cropY,
            cropWidth,
            cropHeight,
            0,
            0,
            cropWidth,
            cropHeight
        );

        canvas.toBlob((blob) => {
            if (blob) {
                if (previewUrl) {
                    URL.revokeObjectURL(previewUrl);
                }
                const url = URL.createObjectURL(blob);
                setPreviewUrl(url);
            }
        });
    };

    const handleCancel = () => {
        setIsModalOpen(false);
        setSelectedFile(null);
        setImageSrc(null);
        const resetCropHeight = cropWidth / aspectRatio;
        setCrop({
            unit: '%',
            width: cropWidth,
            height: resetCropHeight,
            x: 5,
            y: (100 - resetCropHeight) / 2,
        });
        setCompletedCrop(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        imgRef.current = e.currentTarget;
    };

    useEffect(() => {
        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    return (
        <div className={styles.imageUploadContainer}>
            {(previewUrl || imageId) && (
                <div className={styles.currentImage}>
                    {previewUrl ? (
                        <img src={previewUrl} alt="Preview" />
                    ) : (
                        <Image
                            imageOwnerType={ownerType}
                            imageSize={displaySize}
                            imageId={imageId || null}
                        />
                    )}
                </div>
            )}
            <button
                type="button"
                className={globalStyles.submitButton}
                onClick={handleChooseImage}
            >
                {(previewUrl || imageId) ? 'Change Image' : 'Choose Image'}
            </button>

            {isModalOpen && (
                <div className={styles.modal}>
                    <div className={styles.modalContent}>
                        <h2>Upload and Crop Image</h2>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleFileSelect}
                            className={styles.fileInput}
                        />
                        {imageSrc && (
                            <div className={styles.cropContainer}>
                                <ReactCrop
                                    crop={crop}
                                    onChange={(c) => setCrop(c)}
                                    onComplete={(c) => setCompletedCrop(c)}
                                    aspect={aspectRatio}
                                >
                                    <img
                                        ref={imgRef}
                                        src={imageSrc}
                                        alt="Crop preview"
                                        onLoad={onImageLoad}
                                        className={styles.cropImage}
                                    />
                                </ReactCrop>
                            </div>
                        )}
                        <div className={styles.modalButtons}>
                            <button
                                type="button"
                                onClick={handleSave}
                                className={globalStyles.submitButton}
                                disabled={!selectedFile || !completedCrop}
                            >
                                Save
                            </button>
                            <button
                                type="button"
                                onClick={handleCancel}
                                className={globalStyles.cancelButton}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ImageUpload;

